import {
    Button,
    CircularProgress,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    OutlinedInput,
    TextField
} from '@mui/material';
import React, { useState } from 'react';
import MainImage from '../assets/images/login-page.jpg';
import KKLogo from '../assets/images/kk-logo.png';
import TitoLogo from '../assets/images/tito-logo.png';
import { Form, Formik } from 'formik';
import Textfield from '../components/input/Textfield';
import * as Yup from 'yup';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import useScriptRef from '../hooks/useScriptRef';
import { apiRequest } from '../utils/api';
import { jwtDecode } from 'jwt-decode';
import { useDispatch } from 'react-redux';
import { saveUserData } from '../redux/slices/userSlice';

export default function Login() {
    const scriptedRef = useScriptRef();

    const navigate = useNavigate();
    const dispatch = useDispatch();
    // const [checked, setChecked] = useState(true);
    const [loading, setLoading] = useState(false);

    const [showPassword, setShowPassword] = useState(false);
    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const handleLogin = async (email, password) => {
        setLoading(true);
        try {
            const res = await apiRequest('post', 'users/signin', { email, password });
            let user = jwtDecode(res?.data?.token);

            if (user?.isActive && user?.role == 'admin') {
                localStorage.setItem('token', JSON.stringify(res?.data?.token));
                dispatch(saveUserData(user));
                navigate('/');
            } else if (user?.isActive && user?.role == 'super-admin') {
                localStorage.setItem('token', JSON.stringify(res?.data?.token));
                dispatch(saveUserData(user));
                navigate('/');
            } else if (user?.isActive && user?.role == 'data-entry') {
                localStorage.setItem('token', JSON.stringify(res?.data?.token));
                dispatch(saveUserData(user));
                navigate('/de-products');
            } else if (user?.isActive && user?.role == 'item-summary') {
                localStorage.setItem('token', JSON.stringify(res?.data?.token));
                dispatch(saveUserData(user));
                navigate('/de-item-summary');
            } else if (user?.isActive && user?.role == 'account') {
                localStorage.setItem('token', JSON.stringify(res?.data?.token));
                dispatch(saveUserData(user));
                navigate('/a-payments');
            } else if (user?.isActive && user?.role == 'dispatch') {
                localStorage.setItem('token', JSON.stringify(res?.data?.token));
                dispatch(saveUserData(user));
                navigate('/d-reps');
            } else if (user?.isActive && (user?.role == 'payments' || user?.role == 'cash-payments')) {
                localStorage.setItem('token', JSON.stringify(res?.data?.token));
                dispatch(saveUserData(user));
                navigate('/de-payments');
            } else {
                toast.error('Invalid credentials');
            }
        } catch (error) {
            toast.error(error?.response?.data?.message ?? error?.response?.data ?? error?.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Grid container sx={{ height: '100vh' }}>
            <Grid
                item
                lg={6}
                md={6}
                sm={6}
                xs={12}
                sx={{
                    backgroundImage: `url(${MainImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}
            >
                <img src={KKLogo} alt="KK Foods Logo" style={{ height: 200 }} />
                <img src={TitoLogo} alt="Tito Logo" style={{ height: 200 }} />
            </Grid>
            <Grid
                item
                lg={6}
                md={6}
                sm={6}
                xs={12}
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 2 }}
            >
                <h2 style={{ marginBottom: 30 }}>Login</h2>
                <Formik
                    initialValues={{
                        email: '',
                        password: '',
                        submit: null
                    }}
                    validationSchema={Yup.object().shape({
                        email: Yup.string().email('Must be a valid email').max(255).required('Email is required'),
                        password: Yup.string().max(255).required('Password is required')
                    })}
                    onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
                        try {
                            setStatus({ success: true });
                            setSubmitting(false);

                            const { email, password } = values;
                            handleLogin(email.trim().toLowerCase(), password.trim());
                        } catch (err) {
                            console.error(err);
                            if (scriptedRef.current) {
                                setStatus({ success: false });
                                setErrors({ submit: err.message });
                                setSubmitting(false);
                            }
                        }
                    }}
                >
                    {({ errors, handleBlur, handleChange, handleSubmit, touched, values }) => (
                        <form noValidate onSubmit={handleSubmit}>
                            <Grid container direction="row" spacing={2}>
                                <Grid item lg={12} md={6} sm={6} xs={12}>
                                    <FormControl fullWidth error={Boolean(touched.email && errors.email)}>
                                        <InputLabel htmlFor="outlined-adornment-email-login">Email Address</InputLabel>
                                        <OutlinedInput
                                            id="outlined-adornment-email-login"
                                            type="email"
                                            value={values.email}
                                            name="email"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            label="Email Address"
                                            inputProps={{}}
                                        />
                                        {touched.email && errors.email && (
                                            <FormHelperText error id="standard-weight-helper-text-email-login">
                                                {errors.email}
                                            </FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>

                                <Grid item lg={12} md={6} sm={6} xs={12}>
                                    <FormControl fullWidth error={Boolean(touched.password && errors.password)}>
                                        <InputLabel htmlFor="outlined-adornment-password-login">Password</InputLabel>
                                        <OutlinedInput
                                            id="outlined-adornment-password-login"
                                            type={showPassword ? 'text' : 'password'}
                                            value={values.password}
                                            name="password"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            endAdornment={
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={handleClickShowPassword}
                                                        onMouseDown={handleMouseDownPassword}
                                                        edge="end"
                                                        size="large"
                                                    >
                                                        {showPassword ? <Visibility /> : <VisibilityOff />}
                                                    </IconButton>
                                                </InputAdornment>
                                            }
                                            label="Password"
                                            inputProps={{}}
                                        />
                                        {touched.password && errors.password && (
                                            <FormHelperText error id="standard-weight-helper-text-password-login">
                                                {errors.password}
                                            </FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>

                                <Grid item lg={12} md={12} sm={12} xs={12} mt={1}>
                                    <Button disabled={loading} fullWidth type="submit" variant="contained">
                                        {loading ? <CircularProgress size={25} color="inherit" /> : 'Login'}
                                    </Button>
                                </Grid>

                                <Grid item lg={12} md={12} sm={12} xs={12} mt={1}>
                                    <Button onClick={() => navigate(`/privacy-policy`)} fullWidth>
                                        Privacy Policy
                                    </Button>
                                </Grid>
                            </Grid>
                        </form>
                    )}
                </Formik>
            </Grid>
        </Grid>
    );
}
